import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { useCartStore } from '../store/cartStore';
import { createCheckoutSession } from '../lib/stripe';
import { createOrder } from '../firebase/orders';
import { getAuth } from 'firebase/auth';
import toast from 'react-hot-toast';

const steps = ['Información de envío', 'Método de pago', 'Confirmar pedido'];

const Checkout = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const { items, getTotalPrice, clearCart } = useCartStore();
  const [loading, setLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
  });

  const handleNext = () => {
    if (activeStep === 0) {
      const requiredFields = ['firstName', 'lastName', 'email', 'address', 'city', 'postalCode', 'phone'];
      const missingFields = requiredFields.filter(field => !shippingInfo[field]);
      
      if (missingFields.length > 0) {
        toast.error('Por favor, complete todos los campos obligatorios');
        return;
      }

      if (!shippingInfo.email.includes('@')) {
        toast.error('Por favor, ingrese un email válido');
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setShippingInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckout = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Debes iniciar sesión para realizar un pedido');
        return;
      }

      // Create order in Firebase
      const orderData = {
        userId: auth.currentUser.uid,
        items,
        status: 'pending' as const,
        shippingInfo,
        totalAmount: getTotalPrice(),
      };

      await createOrder(orderData);
      await createCheckoutSession(items, shippingInfo.email);
      clearCart();
      navigate('/success');
    } catch (error) {
      console.error('Error during checkout:', error);
      toast.error('Error al procesar el pedido. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Nombre"
                  name="firstName"
                  value={shippingInfo.firstName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Apellidos"
                  name="lastName"
                  value={shippingInfo.lastName}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={shippingInfo.email}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Dirección"
                  name="address"
                  value={shippingInfo.address}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Ciudad"
                  name="city"
                  value={shippingInfo.city}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Código Postal"
                  name="postalCode"
                  value={shippingInfo.postalCode}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  value={shippingInfo.phone}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Método de pago
            </Typography>
            <Typography color="text.secondary">
              Procesando información de pago...
            </Typography>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Resumen del pedido
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                {items.map((item) => (
                  <Card key={item.product.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <img
                            src={item.product.images.find(img => img.isPrimary)?.url}
                            alt={item.product.name}
                            style={{ width: '100%', borderRadius: 8 }}
                          />
                        </Grid>
                        <Grid item xs={9}>
                          <Typography variant="h6">{item.product.name}</Typography>
                          <Typography color="text.secondary">
                            Cantidad: {item.quantity}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ mt: 1 }}>
                            {(item.product.price * item.quantity).toFixed(2)}€
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Resumen
                    </Typography>
                    <Box sx={{ my: 2 }}>
                      <Grid container justifyContent="space-between">
                        <Typography>Subtotal</Typography>
                        <Typography>{getTotalPrice().toFixed(2)}€</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography>Envío</Typography>
                        <Typography>Gratis</Typography>
                      </Grid>
                    </Box>
                    <Divider />
                    <Box sx={{ my: 2 }}>
                      <Grid container justifyContent="space-between">
                        <Typography variant="h6">Total</Typography>
                        <Typography variant="h6">
                          {getTotalPrice().toFixed(2)}€
                        </Typography>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Checkout
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
        {activeStep > 0 && activeStep !== 1 && (
          <Button onClick={handleBack} disabled={loading}>
            Atrás
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Siguiente'
            )}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleCheckout}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Realizar Pedido'
            )}
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default Checkout;